import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, Row, RowLR, Spinner, Text} from "react-vcomponents";
import {TextPlus} from "web-vcore";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {Mod_Viewpoint} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_Viewpoint.js";
import {ResolveObjPosInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {terrainQuadtree_current} from "../../../../../Terrain/Heights/TerrainRenderer.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";

const splitAt = 180;

/** Set15 P0: editor for `Mod_Viewpoint` — the position + orientation of the locked 3D camera.
 *  Mirrors `TerrainSpotHeight_ModUI.tsx` (the `ModifierEditorUI` shared header provides the
 *  modifier-level enabled checkbox / clone / move / delete row). Here we surface only the
 *  viewpoint-specific knobs: eye height, yaw, pitch, fov, roll.
 *
 *  Note: the XY is NOT edited here — it comes from the host `MapObject`'s anchor position
 *  (`ResolveObjPosInWorld`), so the user places the viewpoint by moving the object.
 *
 *  Set15 P5: the camera reads the BAKED absolute `eyeHeight` (stable, no terrain churn).  The
 *  "eye height above terrain" is now a UI-only TARGET (`eyeHeightAboveTerrain_target`); an Apply
 *  button bakes `eyeHeight = terrainHeightAtAnchor + target` so adjusting the target never
 *  auto-moves the camera. */
export const Viewpoint_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, obj, ChangeModWrapper: ChangeMod} = props;
	const Change = (mutator: (data: Mod_Viewpoint)=>any)=>ChangeMod(a=>mutator(a.viewpoint!));
	const data = mod.viewpoint!;
	// The terrain height at the anchor (for the Apply button).  Reads `terrainDataVersion` so this
	// re-runs when the terrain solves; absent chunks fall back to 0 (Apply then bakes the raw target).
	const anchor = obj ? ResolveObjPosInWorld(obj) : null;
	let terrainH = 0;
	if (anchor && terrainQuadtree_current) {
		const sampled = terrainQuadtree_current.sampleHeightAt(anchor.x, anchor.y);
		if (sampled != null) terrainH = sampled;
	}
	const target = data.eyeHeightAboveTerrain_target;

	return (
		<>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					The camera's absolute eye height above world z=0 (metres). Baked and stable — the camera reads
					this directly, independent of the terrain, so it never moves when the terrain solves. Use the
					row below ("eye height above terrain") to set it relative to the terrain, then click Apply.
				`.AsMultiline(0)}>Eye height (absolute, m):</TextPlus>
				<Spinner min={-Infinity} value={data.eyeHeight} onChange={val=>Change(a=>a.eyeHeight = Math.max(0, val))}/>
			</RowLR>
			<Row mt={5}>
				<Button p="5px 10px" text="^ Bake as eye-height" title={`Bake eye height = terrain height at anchor (${terrainH.toFixed(0)} m) + target (${(target ?? 0).toFixed(0)} m) = ${(terrainH + (target ?? 0)).toFixed(0)} m into the absolute eye height.`}
					onClick={()=>{
						const baked = terrainH + (target ?? 0);
						Change(a=>{ a.eyeHeight = Math.max(0, baked); });
					}}/>
				{anchor && <Text ml={5} style={{color: "#aaa", fontSize: 12}}>({terrainH.toFixed(1)}m [terrain] + {(target ?? 0).toFixed(1)}m = {(terrainH + (target ?? 0)).toFixed(1)}m)</Text>}
			</Row>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					UI-only TARGET: the desired camera height ABOVE the terrain. Not read by the camera. Click Apply
					to bake it into the absolute eye height above (= terrain height at this anchor + this target).
					This keeps the camera stable while you experiment with the target.
					Default: 1.6m (average human eye-level).
				`.AsMultiline(0)}>Eye height above terrain (m):</TextPlus>
				<Spinner step={1} min={-Infinity} value={target} onChange={val=>Change(a=>a.eyeHeightAboveTerrain_target = Math.max(0, val))}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					How far from the camera (metres), toward its horizontal/compass direction (the yaw),
					to place the detailed-height-solving region's centre.  The solver (working grid / ring
					stack) centres on this point instead of directly under the camera, so resolution is
					spent where the camera is LOOKING rather than at its feet.  Visualisable in the 2D
					frustum wedge.  Default 0 = centre the solve on the camera (historical behaviour).
				`.AsMultiline(0)}>Detailed-solve centre distance (m):</TextPlus>
				<Spinner min={0} value={data.detailedSolveCenter_distance}
					onChange={val=>Change(a=>a.detailedSolveCenter_distance = Math.max(0, val))}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					PER-VIEWPOINT override of the global render-lattice FLOOR (samples per solved fine-cell
					edge, Set15.5 P2).  "Reset" (default) falls back to the global Terrain-settings value.
					Set it HIGHER than the global default for a viewpoint very close to the ground (which needs
					a finer render lattice to resolve near-field detail), or LOWER to trade resolution for cost
					on a far/wide viewpoint.  Clamped to [0.25, 64]; it is the tree's only depth guard, so it
					cannot be disabled.  This is a 3D-mode-only knob.
				`.AsMultiline(0)}>Render-chunk, max samples/fine-cell-edge:</TextPlus>
				<Spinner min={0.25} max={64} step={0.25}
					value={data.renderChunk_maxSamplesPerFineCellEdge}
					onChange={val=>Change(a=>a.renderChunk_maxSamplesPerFineCellEdge = val)}/>
				<Button ml={5} p="5px 8px" mdIcon="delete" title="Clear this override → fall back to the global Terrain-settings value."
					onClick={()=>Change(a=>a.renderChunk_maxSamplesPerFineCellEdge = null)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Camera yaw (degrees), rotation about world z, measured from map +y (forward/north).
					0 = looking toward +y. The 2D frustum wedge points along this direction.
				`.AsMultiline(0)}>Yaw (°):</TextPlus>
				<Spinner step={5} min={-Infinity} value={data.yaw} onChange={val=>Change(a=>a.yaw = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Camera pitch (degrees). 0 = horizontal; >0 = looking down toward the terrain. This sets
					how far the ground-projected frustum wedge reaches forward in 2D.
				`.AsMultiline(0)}>Pitch (°):</TextPlus>
				<Spinner step={1} min={-Infinity} max={90} value={data.pitch} onChange={val=>Change(a=>a.pitch = val.KeepBetween(0, 90))}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Vertical field of view (degrees). Wider = more of the scene visible = a wider frustum wedge.
				`.AsMultiline(0)}>FOV (°):</TextPlus>
				<Spinner step={1} min={1} max={179} value={data.fov} onChange={val=>Change(a=>a.fov = val.KeepBetween(1, 179))}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Camera roll (degrees), rotation about the view axis. 0 = level horizon. Rarely needed.
				`.AsMultiline(0)}>Roll (°):</TextPlus>
				<Spinner step={1} min={-Infinity} value={data.roll} onChange={val=>Change(a=>a.roll = val)}/>
			</RowLR>
		</>
	);
});
